/**
 * A job, on the shared record page.
 *
 * Addendum §B.1: one status, and billing, collection and work are trackers
 * beside it rather than statuses of their own. So the status is shown once, as
 * a tag next to the title, and the four figures §B.3 defines are the header's
 * pills and the Billing tab.
 *
 * What it replaces is `job-profile.tsx`, which drew its own header, its own
 * tab row, its own two-column grid and its own quote list, and had no
 * Activity, no Files and no phone layout — a job could not be given a note or
 * a photo, which is the one record in Rowted that most needs both. Everything
 * visible here except the mark, the rail and the status moves belongs to
 * `<RecordPage>` and is identical on a contact and a property.
 *
 * `docs/record-page-contract.md` is the rulebook; this file is the third page
 * written to it, which is what makes it a contract rather than a description
 * of two pages.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useParams, useSearchParams, type NavigateFunction } from 'react-router-dom'
import {
  ALLOWED_TRANSITIONS, can, type JobActionItem, type JobActionsAnswer, type JobStatus, type MeResponse,
} from '@rowted/shared'
import { RecordPage, type RecordConfig } from '../record/record-page.js'
import {
  renderStandardTab, standardTabs, type RelatedTabSpec,
} from '../record/record-standard.js'
import {
  AboutCard, QuickContacts, RecordCard, RelatedCard, RelatedRow, type AboutField,
} from '../record/record-rail.js'
import {
  isQuietSummary, quietMenuItems, setQuietHidden,
  type QuietConfig, type QuietStep,
} from '../record/record-quiet.js'
import { completeActivity, useRecordSummary, type RecordSummary } from '../lib/use-record.js'
import { JobForm } from '../components/forms/job-form.js'
import { JobBilling } from '../components/job-billing.js'
import { Avatar, Button, EmptyState, StatusChip, humanize } from '../components/primitives.js'
import { Modal, Timeline, Toast } from '../components/overlays.js'
import type { MenuItem } from '../components/context-menu.js'
import { usePhone } from '../lib/use-narrow.js'
import { TOAST } from '../job-stages/copy.js'
import { JobStagePopups, stageButton, stageMenu, stageMeta, stagePhonePrimary } from './job-stage-header.js'
import { jobMark } from '../components/landing-marks.js'
import {
  IconCompany, IconEdit, IconNext, IconNote, IconPin, IconQuote, IconUpload,
} from '../frame/icons.js'
import {
  EM_DASH, formatDocumentDate, formatDollars, formatRelative, placeLine,
} from '../lib/format.js'
import { request } from '../lib/api.js'
import { mapsLink } from '../lib/maps.js'
import { JobVisitsBand } from './job-visits-band.js'
import { JobByVisitCard } from '../visit/job-by-visit.js'
import { TABS as VISIT_TABS } from '../visit/copy.js'

interface Job {
  id: string
  reference: string
  title: string
  status: JobStatus
  statusReason: string | null
  statusChangedAt: string
  serviceLine: string | null
  /* The division's mark, for the header tile (`list-marks.md` §1). */
  divisionIcon: string | null
  divisionColour: string | null
  jobType: string | null
  reworkOpen: boolean
  reviewedAt: string | null
  source: string
  version: number
  propertyId: string
  propertyName: string
  addressLine1: string
  city: string
  state: string
  accountName: string | null
  salespersonId: string | null
  salespersonName: string | null
  ageInStatusDays: number
  expected: string
  earned: string
  invoiced: string
  received: string
  balance: string
  visitsDone: number
  visitsPlanned: number
  hoursBudget: string
  hoursActual: string
  isOverdue: boolean
  dueOn: string | null
}

interface JobQuote {
  id: string
  reference: string
  status: string
  sentAt: string | null
  total: string
}

interface JobPayload {
  job: Job
  statusHistory: {
    id: string
    fromStatus: string
    toStatus: string
    reason: string | null
    changedAt: string
    changedByName: string | null
  }[]
  quotes: JobQuote[]
  /*
   * The property’s people, not the job’s — a job has no contacts of its own.
   * `contact_property_links` is the join (§4.6), so these are the rows the
   * property page lists, in the same order, primary first.
   */
  contacts: JobContact[]
  nextAction: { text: string; urgent: boolean; rule: string }
}

interface JobContact {
  id: string
  contactId: string
  role: string
  isPrimary: boolean
  isBillTo: boolean
  approvesQuotes: boolean
  accessNote: string | null
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  preferredChannel: string | null
}

/** The shape the config closes over — the record, plus what only this page knows. */
export interface JobRecordData extends JobPayload {
  canEdit: boolean
  canQuote: boolean
  canBill: boolean
  /** `costs_and_margins` is not `none` — the Money tab (Board 12 C's *By visit*) is cost and margin. */
  seesMoney: boolean
  orgUnitId: string
  /** The moves the state machine allows from here, in its own order — part 2's header only. */
  moves: JobStatus[]
  /**
   * Folder 52's buttons (`GET /jobs/:id/actions`, `jobActions()`), for a job
   * in the first half (Requested → Scheduled, Declined, Cancelled); null
   * while they load. Part 2's statuses keep today's header (§ Scope).
   */
  actions: JobActionsAnswer | null
  /** Whether the header is folder 52's (`docs/job-stages.md` §1) rather than today's. */
  staged: boolean
  /** On a phone the header is ⋯ alone, so its menu carries the primary and the secondary (§1). */
  phone: boolean
  /** Users & roles opens for this person — the salesperson in the meta line is then a link (findings §6). */
  seesPeople: boolean
  onAction: (item: JobActionItem) => void
  onEdit: () => void
  onMove: (to: JobStatus) => void
  onQuote: () => void
  onReload: () => void
  onUpload: () => void
  /** The quiet panel's sheet if it is up, the Activity tab if it is not. */
  onLogActivity: () => void
  /** Whether this person has hidden the Set up card here — for the ⋯ menu. */
  quietHidden: boolean
  onShowSetup: () => void
}

/*
 * Quote statuses have no semantic pair of their own in design/tokens.json, so
 * they borrow the job pair that means the same thing. Inventing colours here
 * would put them outside the token pipeline, which derives the dark variants
 * and checks contrast — a palette entry that skipped both would fail neither
 * test and still be wrong.
 */
/**
 * What each move's button says.
 *
 * §3.8: next-action text is a verb. A transition is named by the state it
 * lands in, so a button labelled straight off it reads "Scheduled" — which is
 * a description of where the job would be rather than an instruction to put it
 * there, and sits next to Edit and New quote looking like a status chip
 * somebody can press. Anything not named here falls back to "Mark completed",
 * which is a verb too.
 */
const MOVE_VERBS: Record<string, string> = {
  approved: 'Approve',
  scheduled: 'Schedule',
  in_progress: 'Start work',
  completed: 'Complete',
  declined: 'Decline',
  cancelled: 'Cancel job',
  written_off: 'Write off',
}

const moveVerb = (to: string) => MOVE_VERBS[to] ?? `Mark ${humanize(to).toLowerCase()}`

const QUOTE_STATUS_AS_JOB: Record<string, string> = {
  draft: 'requested',
  sent: 'quoted',
  viewed: 'reviewed',
  approved: 'approved',
  declined: 'declined',
  expired: 'cancelled',
}

/**
 * The statuses folder 52 draws the header for (`docs/job-stages.md` §2, Board
 * 1). Part 2's — In progress, Completed, Invoiced, Paid, Written off — keep
 * today's header exactly, `MOVE_VERBS` and all, until part 2 replaces it.
 */
const FIRST_HALF = new Set<string>(['requested', 'quoted', 'approved', 'scheduled', 'declined', 'cancelled'])

export function JobRecord({ me }: { me: MeResponse }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<JobPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [people, setPeople] = useState<{ id: string; name: string }[]>([])
  const [editing, setEditing] = useState(false)
  const [moving, setMoving] = useState<JobStatus | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [completing, setCompleting] = useState<string | null>(null)
  /* Folder 52: the buttons, the popup one of them opened, and the white toast after a move. */
  const [actions, setActions] = useState<JobActionsAnswer | null>(null)
  const [popup, setPopup] = useState<JobActionItem | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const phone = usePhone()

  const openUpload = useRef<(() => void) | null>(null)
  /* The quiet panel's log-activity sheet, lent upwards — see the property
     page for why it is a ref read at click time rather than a flag. */
  const openLog = useRef<(() => void) | null>(null)

  const { summary, reload: reloadSummary } = useRecordSummary('job', id || undefined)

  const load = useCallback(() => {
    request<JobPayload>(`/jobs/${id}`)
      .then((payload) => { setData(payload); setError(null) })
      .catch((cause: unknown) => setError(
        cause instanceof Error ? cause.message : 'That job could not be loaded.'))
  }, [id])

  useEffect(() => { load() }, [load])

  /* `jobActions()` as the server works it out (`docs/job-stages.md` §1) — read beside the job, never guessed here. */
  const loadActions = useCallback(() => {
    request<JobActionsAnswer>(`/jobs/${id}/actions`)
      .then(setActions)
      .catch(() => setActions(null))
  }, [id])

  useEffect(() => { loadActions() }, [loadActions])

  useEffect(() => {
    void request<{ items: { id: string; name: string }[] }>('/people?limit=200')
      .then((body) => setPeople(body.items))
      .catch(() => setPeople([]))
  }, [])

  const canEdit = me.permissions.jobs === 'edit' || me.permissions.jobs === 'full'
  /* Making a missing visit (`docs/visit-page.md` §26.5); cancelling one is the visit header's ⋯ (§26.7). */
  const canVisit = me.permissions.visits === 'edit' || me.permissions.visits === 'full'
  const [params] = useSearchParams()
  const canQuote = me.permissions.quotes === 'edit' || me.permissions.quotes === 'full'
  /* Booking a crew is its own permission — §7, "absent, not greyed". */
  const canSchedule = me.permissions.schedule === 'edit' || me.permissions.schedule === 'full'
  /*
   * Billing is an Invoices permission, not a Jobs one: somebody who can
   * schedule work is not necessarily somebody who can decide how it is charged
   * for. The server gates it the same way.
   */
  const canBill = me.permissions.invoices === 'edit' || me.permissions.invoices === 'full'
  const seesMoney = me.permissions.costs_and_margins !== 'none'
  const orgUnitId = me.scope.selected[0] ?? ''

  const complete = useCallback(async (activityId: string) => {
    setCompleting(activityId)
    try {
      /* One occurrence of a repeating meeting by itself (`completeKey`, 0152). */
      await completeActivity(activityId)
      await reloadSummary()
    } finally {
      setCompleting(null)
    }
  }, [reloadSummary])

  const startQuote = useCallback(async () => {
    if (!data) return
    setBusy(true)
    try {
      const created = await request<{ id: string }>('/quotes', {
        method: 'POST', body: { jobId: data.job.id },
      })
      navigate(`/quotes/${created.id}`)
    } finally {
      setBusy(false)
    }
  }, [data, navigate])

  /** Reopen request (§5): no popup — the job is a request again, and the white toast says so. */
  const reopen = useCallback(async () => {
    if (!data) return
    setBusy(true)
    try {
      await request(`/jobs/${data.job.id}/reopen`, { method: 'POST', body: {} })
      setNotice(TOAST.requestAgain(data.job.reference))
      load()
      loadActions()
      void reloadSummary()
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }, [data, load, loadActions, reloadSummary])

  /** What each of Board 1's buttons and ⋯ rows does (§5). */
  const onAction = useCallback((item: JobActionItem) => {
    switch (item.key) {
      case 'edit': setEditing(true); return
      case 'new_quote': void startQuote(); return
      case 'open_quote':
      case 'open_draft':
      case 'open_version':
      case 'open_agreement':
        if (item.quote) navigate(`/quotes/${item.quote.id}`)
        return
      /* The Schedule, focused on this job: its visits to book in the queue, its booked days on the board. */
      case 'schedule': navigate(`/schedule?job=${encodeURIComponent(id)}`); return
      /* Today's moves, unchanged, until part 2. */
      case 'start_work': setMoving('in_progress'); setReason(''); return
      case 'complete': setMoving('completed'); setReason(''); return
      case 'reopen_request': void reopen(); return
      /* Board 3's popups. */
      case 'mark_quoted':
      case 'approve':
      case 'withdraw_quote':
      case 'mark_declined':
      case 'cancel_job':
        setPopup(item)
        return
      default:
    }
  }, [id, navigate, reopen, startQuote])

  const record = useMemo<JobRecordData | null>(() => (data ? {
    ...data,
    canEdit,
    canQuote,
    canBill,
    seesMoney,
    orgUnitId,
    // Only the moves the state machine allows are offered, so a button never
    // produces a 400 (§E.2).
    moves: ALLOWED_TRANSITIONS.job
      .filter((transition) => transition.from === data.job.status)
      .map((transition) => transition.to as JobStatus),
    actions,
    staged: FIRST_HALF.has(data.job.status) && actions?.asToday !== true,
    phone,
    seesPeople: can(me.permissions, 'employees', 'view'),
    onAction,
    onEdit: () => setEditing(true),
    onMove: (to: JobStatus) => { setMoving(to); setReason('') },
    onQuote: () => void startQuote(),
    onReload: () => { load(); loadActions(); void reloadSummary() },
    onUpload: () => openUpload.current?.(),
    onLogActivity: () => {
      if (openLog.current) openLog.current()
      else navigate(`/jobs/${id}?tab=activity`)
    },
    quietHidden: summary?.quiet?.hidden ?? false,
    onShowSetup: () => {
      void setQuietHidden('job', id, false).then(() => reloadSummary())
    },
  } : null), [
    data, canEdit, canQuote, canBill, seesMoney, orgUnitId, id, navigate, load, reloadSummary,
    startQuote, summary?.quiet?.hidden, actions, phone, me.permissions, onAction, loadActions])

  const config = useMemo(
    () => jobConfig(navigate, { schedule: canSchedule }), [navigate, canSchedule])

  /*
   * The pills are money, and `costs_and_margins: none` means somebody is not
   * shown money. The summary endpoint does not know that — it returns the
   * three figures for every caller who may see the job at all — so the gate is
   * here, where the permission is, rather than by hiding the header.
   */
  const shown = useMemo<RecordSummary | null>(
    () => (summary && !seesMoney ? { ...summary, pills: [] } : summary),
    [summary, seesMoney])

  const needsReason = moving && data
    ? ALLOWED_TRANSITIONS.job.find(
      (transition) => transition.from === data.job.status && transition.to === moving,
    )?.requiresReason === true
    : false

  async function move() {
    if (!moving || !record) return
    setBusy(true)
    try {
      await request(`/jobs/${record.job.id}/transition`, {
        method: 'POST',
        body: { to: moving, reason: reason.trim() || undefined },
      })
      setMoving(null)
      setReason('')
      record.onReload()
    } finally {
      setBusy(false)
    }
  }

  if (error) {
    return (
      <EmptyState
        title="Not found"
        body={error}
        action={{ label: 'Back to jobs', onClick: () => navigate('/jobs') }}
      />
    )
  }
  if (!record) return <p className="p-4 text-cell text-text-muted">Loading…</p>

  const { job } = record

  /*
   * The Visits tab: the quote check's band directly under the tab's own
   * header and above the list (§26.5, Board 17 C and E). The list is the
   * list engine's, untouched; the band is this page's.
   */
  const withVisitsBand = (tab: string, content: ReactNode) => (tab === 'visits'
    ? (
      <div className="flex flex-col gap-3">
        <JobVisitsBand
          jobId={job.id}
          canCreate={canVisit}
          onOpenQuote={(quoteId) => navigate(`/quotes/${quoteId}`)}
          onCreated={() => record.onReload()}
        />
        {content}
      </div>
    )
    : content)

  return (
    <>
      <RecordPage config={config} record={record} summary={shown}>
        {(tab) => withVisitsBand(tab, renderStandardTab(tab, {
          object: 'job',
          id: job.id,
          href: `/jobs/${job.id}`,
          summary: shown,
          related: relatedTabs(record, navigate),
          people,
          orgUnitId,
          /*
           * `job_status_history` is not an audit table — it is the job's own
           * record of its moves — so the History tab is told about it and the
           * rail's Status history card reads the same rows from the payload.
           */
          history: {
            table: 'jobs',
            related: {
              tables: ['job_status_history'],
              recordIds: record.statusHistory.map((step) => step.id),
            },
          },
          busyId: completing,
          canEdit,
          onComplete: (activityId) => void complete(activityId),
          onChanged: () => void reloadSummary(),
          onUploadReady: (open) => { openUpload.current = open },
          onLogReady: (open) => { openLog.current = open },
          quiet: config.quiet?.build(record, summary) ?? null,
          onOpen: (path) => navigate(path),
        }) ?? (
          tab === 'billing'
            ? <JobBilling jobId={job.id} canEdit={canBill} />
            : tab === 'money' && seesMoney
              ? <JobByVisitCard jobId={job.id} highlight={params.get('visit')} onOpen={(path) => navigate(path)} />
              : null
        ))}
      </RecordPage>

      <JobForm
        open={editing}
        onClose={() => setEditing(false)}
        existing={job}
        onSaved={() => { setEditing(false); record.onReload() }}
      />

      <Modal
        open={moving !== null}
        onClose={() => setMoving(null)}
        title={moving ? `Move to ${humanize(moving)}` : ''}
        footer={(
          <>
            <Button onClick={() => setMoving(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={busy || (needsReason && !reason.trim())}
              onClick={() => void move()}
            >
              {busy ? 'Moving…' : 'Move'}
            </Button>
          </>
        )}
      >
        <p className="m-0 mb-2 text-cell text-text-muted">
          {humanize(job.status)} → {moving ? humanize(moving) : ''}
        </p>
        {needsReason && (
          <label className="block">
            <span className="mb-1 block text-sub text-text-muted">
              Why? This is kept with the job.
            </span>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              autoFocus
              className="w-full rounded-field border border-line bg-surface p-2 text-cell text-text"
            />
          </label>
        )}
      </Modal>

      {/* Board 3's popups, from the buttons and ⋯; each reloads the job and may leave a toast. */}
      <JobStagePopups
        jobId={job.id}
        item={popup}
        actions={actions}
        phone={phone}
        onClose={() => setPopup(null)}
        onDone={(toast) => {
          setPopup(null)
          if (toast) setNotice(toast)
          record.onReload()
        }}
      />

      {/* The white toast after a state change (§5, findings §11). */}
      {notice && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </>
  )
}

/**
 * The two lists a job has.
 *
 * Not three: a job's related *jobs* are itself, so a Jobs tab here would open
 * onto one row, the row you are already reading. The Overview's peeks follow
 * the same array, which is what keeps that card off the page too.
 */
function relatedTabs(record: JobRecordData, navigate: NavigateFunction): RelatedTabSpec[] {
  const on = [{ field: 'jobId', op: 'eq' as const, value: record.job.id }]
  return [
    {
      area: 'visits',
      listKey: 'visits@job',
      baseFilter: on,
      /* Folder 32 §3.1's Job › Visits columns (`visits-01`): Visit · Date · Days · Crew · Status · Est h · Actual · Invoice. */
      fields: ['description', 'scheduledDate', 'days', 'crewName', 'status', 'budgetedHours', 'actualHours', 'invoiceReference'],
      emptyBody: 'Scheduling this job’s work puts its visits here.',
      ...(record.canEdit
        ? { onNew: { label: 'Schedule work', onClick: () => navigate(`/schedule?job=${record.job.id}`) } }
        : {}),
    },
    {
      area: 'invoices',
      listKey: 'invoices@job',
      baseFilter: on,
      emptyBody: 'An invoice for this job shows up here.',
      ...(record.canBill
        ? { onNew: { label: 'New invoice', onClick: () => navigate('/invoices/new') } }
        : {}),
    },
  ]
}

// ---------------------------------------------------------------------------
// The quiet page
// ---------------------------------------------------------------------------

/**
 * What a job with nothing on it says — `quiet-records.md` §7.
 *
 * **The lead moves with the status, and it is the only one that does.** A
 * requested job is waiting for a price; an approved one is waiting for a day
 * and a crew. Nothing else about the panel changes — the steps stay in the
 * same order and the same words — because `quietProgress` leads with the
 * first one still to do and the quote step becomes *done* at approval. That
 * is §11's third decision taken the plain way: the rule is "the quote is
 * done or not applicable", which falls out of `state` rather than needing a
 * second rule beside it.
 *
 * The quote step is the only one in the product that **changes its own
 * words**: with a draft or a sent quote it is "Open the quote" and its line
 * is that quote's status and total, because by then the step is not asking
 * for one — it is pointing at the one that exists.
 */
export function jobQuiet(
  record: JobRecordData,
  can: { schedule: boolean },
  navigate: NavigateFunction,
  /** What is connected to it — the file count is only here. */
  summary: RecordSummary | null,
): QuietConfig {
  const { job, quotes, contacts, canEdit } = record
  const open = quotes.find((quote) => quote.status !== 'declined' && quote.status !== 'expired')
  const approved = quotes.some((quote) => quote.status === 'approved')
  const files = summary?.counts.files ?? 0
  /*
   * **A finished job is not being set up.** This is the one record in the
   * product with an end, and without this line it is also the one that
   * carries a Set up card for ever: "Attach files" is never *done* on its
   * own, so `todo` is never empty and only `SETUP_MAX_THINGS` retires the
   * card — which a job with four visits and an invoice on it is nowhere
   * near. A paid job being told to schedule a visit is exactly the furniture
   * §11 warns about, and it is worse than furniture because it is wrong.
   */
  const over = FINISHED_JOB.has(job.status)
  const left = (state: QuietStep['state']): QuietStep['state'] =>
    (over && state === 'todo' ? 'absent' : state)

  const steps: QuietStep[] = [
    {
      key: 'quote',
      icon: 'quote',
      title: open && !approved ? 'Open the quote' : 'Write the quote',
      /* The total as the Quotes card beside it prints it — *$1,792*, never the column's *1792.00*. */
      body: open && !approved
        ? `${open.reference} · ${humanize(open.status)} · ${formatDollars(Number(open.total))}`
        : 'Price the work from the catalogue; its hours become the schedule’s budget.',
      state: left(!record.canQuote ? 'absent' : approved ? 'done' : 'todo'),
      doneBody: 'Approved — the work and its budget are set.',
      onSelect: open ? () => navigate(`/quotes/${open.id}`) : record.onQuote,
    },
    {
      key: 'visit',
      icon: 'visits',
      title: 'Schedule a visit',
      body: 'Pick a day and a crew.',
      /* Done once the job has days on the board; there is nothing to book
         on a job whose visits are already scheduled. */
      state: left(!can.schedule
        ? 'absent'
        : job.visitsPlanned > 0 ? 'done' : 'todo'),
      doneBody: job.visitsPlanned === 1
        ? '1 visit on the board'
        : `${job.visitsDone} of ${job.visitsPlanned} visits done`,
      /* The Schedule focused on this job (`docs/job-stages.md` §5 Schedule; the board takes `?job=`). */
      onSelect: () => navigate(`/schedule?job=${job.id}`),
    },
    {
      key: 'details',
      icon: 'details',
      title: 'Finish the details',
      body: job.jobType ? 'Everything About asks for is filled in.' : 'Type is blank.',
      state: left(!canEdit ? 'absent' : job.jobType ? 'done' : 'todo'),
      doneBody: 'Type, division and salesperson are all set.',
      onSelect: () => navigate(`/jobs/${job.id}?focus=about`),
    },
    {
      key: 'files',
      icon: 'files',
      kind: 'files',
      title: 'Attach files',
      body: 'Drop site photos or plans anywhere on this page.',
      phone: { title: 'Add photos or files', body: 'Site photos or plans.' },
      state: left(!canEdit ? 'absent' : files > 0 ? 'done' : 'todo'),
      doneBody: files === 1 ? '1 file' : `${files} files`,
    },
    {
      key: 'contacts',
      icon: 'contacts',
      title: 'Add a contact',
      /* Board 3: done, it says what is true — *Contacts added · Derek Lombardi and 1 more*. */
      doneTitle: 'Contacts added',
      body: 'Who approves the quote and who to call on site.',
      /*
       * A job has no contacts of its own — these are the *property's* people
       * (§4.6), which is why the step opens the property rather than a form
       * here. Done the moment there is one, because that is a fact about the
       * property and nothing on this page can change it.
       */
      state: left(!canEdit ? 'absent' : contacts.length > 0 ? 'done' : 'todo'),
      doneBody: contacts.length === 1
        ? `${contacts[0]!.firstName} ${contacts[0]!.lastName}`.trim()
        /* Board 3: *Derek Lombardi and 1 more* — the first one's whole name. */
        : `${`${contacts[0]?.firstName ?? ''} ${contacts[0]?.lastName ?? ''}`.trim()} and ${contacts.length - 1} more`,
      onSelect: () => navigate(`/properties/${job.propertyId}`),
    },
  ]

  return {
    headline: 'Nothing has happened on this job yet',
    sub: JOB_QUIET_SUBS[job.status] ?? JOB_QUIET_SUBS.requested!,
    composerSubject: 'this job',
    setupSubject: 'this job',
    steps,
    ghosts: [
      { title: 'Next up', line: 'Calls and tasks waiting on this job.', icon: 'clock' },
      { title: 'Visits', line: `Days and crews at ${job.addressLine1}.`, icon: 'visits' },
      { title: 'Notes', line: 'What was agreed, and what the crew should know.', icon: 'note' },
    ],
    canWrite: canEdit,
  }
}

/**
 * The sub-line, by status — §7.
 *
 * Only three statuses have their own, because only three of them are a job
 * that is still quiet: past Approved there is a visit or an invoice on it and
 * the panel is gone. Anything else falls back to the Requested line, which is
 * the one that assumes least.
 */
const JOB_QUIET_SUBS: Partial<Record<string, string>> = {
  requested: 'It is a new request. Price it, book a crew or write down what you know.',
  quoted: 'The quote is out. Book a crew when it is approved, or write down what you know.',
  approved: 'The quote is approved. Book a crew.',
}

/**
 * The statuses past which a job is not being set up.
 *
 * Completed and everything after it, plus the three exits. A job in any of
 * them has either had its work done or stopped having any — the steps that
 * are not already finished are things nobody is going to do, so they are
 * absent rather than waiting (§1 rule 4, applied to time rather than to
 * permission).
 */
const FINISHED_JOB = new Set<string>([
  'completed', 'invoiced', 'paid', 'declined', 'cancelled', 'written_off',
])

// ---------------------------------------------------------------------------
// The configuration
// ---------------------------------------------------------------------------

function jobConfig(
  navigate: NavigateFunction,
  /** What this viewer may do elsewhere — scheduling is its own permission. */
  can: { schedule: boolean },
): RecordConfig<JobRecordData> {
  const mapsOf = (job: Job) => mapsLink([job.addressLine1, job.city, job.state])

  return {
    object: 'job',

    breadcrumb: (record) => [
      { label: 'Jobs', href: '/jobs' },
      { label: record.job.reference },
    ],

    /*
     * The square is the job's status, the way a visit's square is its date
     * and an invoice's is where it has got to. A job has no picture and no
     * place of its own — it is a piece of work at somebody else's address —
     * so the one thing worth 56px is where it is in its life.
     */
    /*
     * The same tile the Jobs board draws, at 56 (`marks-and-icons.md` §7).
     *
     * It was a page-specific drawing — the nav's job glyph over a status tint
     * with the reference under it — which meant the header and the row said
     * different things about the same job. One function at two sizes is the
     * rule now, and the reference it used to carry is the breadcrumb and the
     * title beside it anyway.
     */
    mark: (record) => jobMark({
      id: record.job.id,
      status: record.job.status,
      serviceLine: record.job.serviceLine,
      divisionIcon: record.job.divisionIcon,
      divisionColour: record.job.divisionColour,
      jobType: record.job.jobType,
    }, 56),

    title: (record) => record.job.title,

    /*
     * The status, and the rework flag when it is up. §B.1's whole point is
     * that `Returned` is a flag on a Completed job rather than a status of its
     * own, which is exactly what two chips side by side say and one chip
     * reading "Returned" would not.
     */
    tags: (record) => [
      {
        label: humanize(record.job.status),
        tone: {
          fg: `rgb(var(--status-job-${record.job.status}-fg))`,
          bg: `rgb(var(--status-job-${record.job.status}-bg))`,
        },
      },
      ...(record.job.reworkOpen
        ? [{
          label: 'Returned',
          tone: {
            fg: 'rgb(var(--status-job-returned-fg))',
            bg: 'rgb(var(--status-job-returned-bg))',
          },
        }]
        : []),
    ],

    /*
     * Next action first. The meta line loses items from the right when it does
     * not fit, and on a job the one fact that is worth the width is what
     * somebody is supposed to do about it — the same sentence the board's
     * Next action column prints, from the same rules.
     */
    meta: (record) => {
      const { job } = record
      const maps = mapsOf(job)
      return [
        ...(record.nextAction.text
          ? [{
            icon: <IconNext size={13} />,
            text: `${record.nextAction.text} · ${job.ageInStatusDays}d in status`,
          }]
          : []),
        {
          icon: <IconPin size={13} />,
          text: placeLine(job.propertyName, job.addressLine1, job.city),
          onClick: () => navigate(`/properties/${job.propertyId}`),
        },
        ...(job.accountName ? [{ icon: <IconCompany size={13} />, text: job.accountName }] : []),
        /*
         * The salesperson is a link to that person (findings §6) — where Sales
         * coverage sends a name, their Profile under Users & roles — for
         * whoever may open that page; plain words for anybody else.
         */
        ...(job.salespersonName
          ? [{
            text: job.salespersonName,
            ...(record.seesPeople && job.salespersonId
              ? { onClick: () => navigate(`/settings/people?person=${job.salespersonId}&tab=profile`) }
              : {}),
          }]
          : []),
        /* Folder 52, Board 1: the quote with the customer (Quoted), *<n> of <m> visits booked* (Approved, Scheduled). */
        ...(record.staged && record.actions
          ? stageMeta(record.actions, (quoteId) => navigate(`/quotes/${quoteId}`))
          : []),
        ...(maps ? [{ text: 'Directions', href: maps.href }] : []),
      ]
    },

    /*
     * Folder 52 (`docs/job-stages.md` §1, Board 1): the secondary white, the
     * primary green right-most beside ⋯, everything else in ⋯ with Edit
     * first — all of it `jobActions()`'s, from `GET /jobs/:id/actions`. The
     * raw state-machine moves are gone for these six statuses: nothing but
     * what happens to the job's quotes and visits moves them (§2).
     */
    headerButtons: (record) => (record.staged ? 'stages' : null),

    /*
     * Part 2's statuses: today's header. The primary is the next move the
     * state machine allows. It is the one button on a job that changes what
     * the job *is*, and every other page's primary is likewise the thing that
     * page exists to do.
     */
    primary: (record) => {
      if (record.staged) {
        return record.actions?.primary ? stageButton(record.actions.primary, record.actions, record.onAction) : null
      }
      return record.canEdit && record.moves[0]
        ? {
          label: moveVerb(record.moves[0]),
          onClick: () => record.onMove(record.moves[0]!),
        }
        : null
    },

    actions: (record) => {
      if (record.staged) {
        return record.actions?.secondary
          ? [stageButton(record.actions.secondary, record.actions, record.onAction)]
          : []
      }
      return [
        ...(record.canEdit
          ? [{ label: 'Edit', icon: <IconEdit size={14} />, onClick: record.onEdit }]
          : []),
        ...(record.canQuote
          ? [{ label: 'New quote', icon: <IconQuote size={14} />, onClick: record.onQuote }]
          : []),
      ]
    },

    /*
     * Folder 52: `more`'s rows (on a phone the primary and the secondary
     * first), then today's quiet-record items at the end (§1).
     *
     * Part 2: the rest of the legal moves, by name. The old page showed the
     * first two and silently dropped the others, so "Cancel" was unreachable
     * from a job that had three ways forward — a state machine with edges
     * nothing can traverse.
     */
    menu: (record) => {
      const items: MenuItem[] = []
      if (record.staged) {
        if (record.actions) items.push(...stageMenu(record.actions, record.onAction, { phone: record.phone, page: 'job' }))
      } else if (record.canEdit) {
        for (const to of record.moves.slice(1)) {
          items.push({
            key: to,
            label: moveVerb(to),
            onSelect: () => record.onMove(to),
          })
        }
      }
      items.push(...quietMenuItems(record.quietHidden, record.onShowSetup))
      return items
    },

    tabs: (record, summary) => standardTabs({
      counts: summary?.counts ?? {},
      related: relatedTabs(record, navigate),
      /*
       * A tab rather than a card on the Overview because it is five kinds of
       * schedule and a preview of what will be sent, and because most of the
       * time nobody is looking at it (§6.9).
       */
      own: [
        /*
         * Money (docs/visit-page.md §16.4, Board 12 C): the visits' actuals,
         * *By visit* — every cost posts to a visit; the job is the sum. The
         * card and nothing else.
         */
        ...(record.seesMoney ? [{ key: 'money', label: VISIT_TABS.money, icon: 'dollar' as const }] : []),
        ...(record.canBill
        ? [{
          key: 'billing',
          label: 'Billing',
          icon: 'invoices' as const,
          /*
           * Wide, like the list tabs. Billing is three panels — the schedule,
           * what the next run would send, and what it has sent — and squeezed
           * beside a 340px rail its switch rows lose their labels. It is also
           * a workspace rather than a fact about the job, which is what the
           * rail is for.
           */
          wide: true,
        }]
        : []),
      ],
    }),

    rail: (record) => <JobRail record={record} navigate={navigate} />,

    quiet: {
      when: (record, summary) => isQuietSummary(summary, relatedTabs(record, navigate)),
      build: (record, summary) => jobQuiet(record, can, navigate, summary),
    },

    phone: {
      /*
       * One: the address. Somebody opening a job on a phone is deciding where
       * to drive, and the people to call are on the property, one tap away.
       */
      quickActions: (record) => {
        const maps = mapsOf(record.job)
        return maps
          ? [{ label: 'Navigate', icon: <IconPin size={18} />, href: maps.href }]
          : []
      },
      tabBar: ['overview', 'activity', 'visits', 'invoices'],
      primaryByTab: (tab, record) => {
        if (tab === 'activity') return null
        if (tab === 'files' || tab === 'overview') {
          if (!record.canEdit) return null
          return tab === 'files'
            ? { label: 'Upload', icon: <IconUpload size={18} />, onClick: record.onUpload }
            : {
              label: 'Log activity',
              icon: <IconNote size={18} />,
              /* The panel's own sheet while it is up — the record decides. */
              onClick: record.onLogActivity,
            }
        }
        /* Folder 52 (§1): `jobActions()`'s primary, which already leaves out what this role can't use. */
        if (record.staged) return record.actions ? stagePhonePrimary(record.actions, record.onAction) : null
        if (!record.canEdit) return null
        return record.moves[0]
          ? {
            label: moveVerb(record.moves[0]),
            icon: <IconNext size={18} />,
            onClick: () => record.onMove(record.moves[0]!),
          }
          : null
      },
    },
  }
}

// ---------------------------------------------------------------------------
// A job's own pieces
// ---------------------------------------------------------------------------

function JobRail({
  record, navigate,
}: {
  record: JobRecordData
  navigate: NavigateFunction
}) {
  const { job } = record

  const fields: AboutField[] = [
    { label: 'Service line', value: job.serviceLine || EM_DASH },
    { label: 'Type', value: job.jobType || EM_DASH },
    { label: 'Came from', value: humanize(job.source) },
    {
      label: 'Reviewed',
      value: job.reviewedAt ? formatDocumentDate(job.reviewedAt) : 'Not yet',
    },
    { label: 'Salesperson', value: job.salespersonName ?? EM_DASH },
    {
      label: 'Visits',
      value: job.visitsPlanned > 0 ? `${job.visitsDone} of ${job.visitsPlanned}` : EM_DASH,
    },
    {
      label: 'Hours',
      value: Number(job.hoursBudget) > 0
        ? `${Number(job.hoursActual).toFixed(1)} of ${Number(job.hoursBudget).toFixed(1)} h`
        : EM_DASH,
    },
  ]

  return (
    <>
      {/* `?focus=about` is where "Finish the details" lands (§7). */}
      <AboutCard
        fields={fields}
        onEdit={record.canEdit ? record.onEdit : undefined}
        focusKey="about"
        {...(record.canEdit ? { onFocus: record.onEdit } : {})}
      />

      {/*
        * Who to ring about this work.
        *
        * The property is in the header and one click away, but the *people*
        * were two: open the property, then read its contacts card. A job is
        * work at a place on a day and the question it raises most often is
        * "who do I call" — so the same rows the property lists are here,
        * carrying Call / Text / Email instead of the notification grid. Who
        * hears about what is a thing you maintain on the property; on a job
        * it is never the question.
        *
        * No Add button: a contact belongs to the *property*, and adding one
        * from here would look like adding it to the job.
        */}
      <RelatedCard title="Contacts" count={record.contacts.length}>
        {record.contacts.length === 0
          ? (
            <p className="m-0 px-[18px] py-3 text-body text-text-muted">
              Nobody is on {record.job.propertyName} yet. Add them on the property and
              they show up here.
            </p>
          )
          : record.contacts.map((link) => {
            const name = `${link.firstName} ${link.lastName}`.trim()
            return (
              <RelatedRow
                key={link.id}
                mark={<Avatar name={name} size={34} />}
                title={name}
                sub={(
                  <>
                    {link.role}
                    {link.isPrimary && ' · primary'}
                    {link.isBillTo && ' · bill to'}
                    {link.approvesQuotes && ' · approves quotes'}
                  </>
                )}
                dots={(
                  <QuickContacts
                    phone={link.phone}
                    email={link.email}
                    preferred={link.preferredChannel}
                  />
                )}
                onOpen={() => navigate(`/contacts/${link.contactId}`)}
              />
            )
          })}
      </RelatedCard>

      <RecordCard
        title="Quotes"
        count={record.quotes.length}
        /* Today's look until folder 51 redraws it; once a quote is approved there is no New (§3, `409 agreement_exists`). */
        action={record.canQuote && !record.quotes.some((quote) => quote.status === 'approved')
          ? { label: 'New', icon: <IconQuote size={13} />, onClick: record.onQuote }
          : undefined}
        footer="An approved quote is locked; a change is a new version."
      >
        {record.quotes.length === 0
          ? (
            <p className="m-0 px-[18px] py-4 text-body text-text-muted">
              No quote yet. Price the work from the catalogue — the hours a service carries
              become the schedule’s budget.
            </p>
          )
          : record.quotes.map((quote) => (
            <RelatedRow
              key={quote.id}
              mark={(
                <span
                  aria-hidden
                  className="flex h-[26px] w-[26px] items-center justify-center rounded-field"
                  style={{
                    background: `rgb(var(--status-job-${QUOTE_STATUS_AS_JOB[quote.status] ?? 'requested'}-bg))`,
                    color: `rgb(var(--status-job-${QUOTE_STATUS_AS_JOB[quote.status] ?? 'requested'}-fg))`,
                  }}
                >
                  <IconQuote size={14} />
                </span>
              )}
              title={quote.reference}
              trailing={(
                <span className="tnum text-body font-semibold text-text">
                  {formatDollars(Number(quote.total))}
                </span>
              )}
              sub={(
                <StatusChip
                  group="job"
                  status={QUOTE_STATUS_AS_JOB[quote.status] ?? 'requested'}
                >
                  {humanize(quote.status)}
                </StatusChip>
              )}
              onOpen={() => navigate(`/quotes/${quote.id}`)}
            />
          ))}
      </RecordCard>

      {/*
        * Where the job has been, from `job_status_history` rather than from
        * the audit log. They are different questions: the audit log says every
        * column that ever changed, and this says the six moves somebody would
        * describe the job by.
        */}
      <RecordCard title="Status history" count={record.statusHistory.length}>
        <div className="px-[18px] py-2">
          {record.statusHistory.length === 0
            ? <p className="m-0 py-2 text-body text-text-muted">Still where it started.</p>
            : (
              <Timeline
                entries={record.statusHistory.map((step) => ({
                  id: step.id,
                  at: formatRelative(step.changedAt),
                  who: step.changedByName ?? 'Rowted',
                  what: `${humanize(step.fromStatus)} → ${humanize(step.toStatus)}`,
                  ...(step.reason ? { detail: step.reason } : {}),
                }))}
              />
            )}
        </div>
      </RecordCard>
    </>
  )
}
